import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, UserPlus, Send } from "lucide-react";
import { useAuth } from "../AuthContext";
import { API_URL } from "../config";

/**
 * Плашка ответственного редактора раздела. Показывает либо имя врача, отвечающего
 * за специальность, либо честную вакансию с формой отклика — пустой раздел
 * так читается как приглашение, а не как заброшенный.
 */
export default function SpecialtyEditorBanner({ specialty, specialtyLabel, onLoginRequest }) {
  const { isLoggedIn, token } = useAuth();
  const [editors, setEditors] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const load = useCallback(() => {
    if (!specialty) { setEditors(null); return; }
    setSent(false);
    setShowForm(false);
    fetch(`${API_URL}/specialty-editors/${specialty}`)
      .then(r => r.ok ? r.json() : null)
      .then(section => setEditors(section?.editors ?? []))
      .catch(() => setEditors([]));
  }, [specialty]);

  useEffect(() => { load(); }, [load]);

  const apply = async e => {
    e.preventDefault();
    if (!isLoggedIn) { onLoginRequest && onLoginRequest(); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/specialty-editors/${specialty}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: message.trim() || null }),
      });
      if (r.ok) { setSent(true); setShowForm(false); setMessage(""); }
    } finally {
      setBusy(false);
    }
  };

  if (!specialty || !editors) return null;

  // Раздел с назначенным(и) редактором(ами)
  if (editors.length > 0) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", gap: 10, marginBottom: 18,
        padding: "12px 16px", background: "#f7f8fc", border: "1px solid #e2e4f0", borderRadius: 10,
      }}>
        <div style={{ fontSize: "0.76rem", color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {editors.length > 1 ? "Ответственные редакторы раздела" : "Ответственный редактор раздела"}
        </div>
        {editors.map(e => (
          <div key={e.editor_id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {e.avatar_url ? (
              <img src={e.avatar_url} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 44, height: 44, borderRadius: "50%", background: "#007c84", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0,
              }}>
                {(e.display_name || "?")[0].toUpperCase()}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                <Link to={`/doctors/${e.editor_id}`} style={{ fontWeight: 700, color: "#007c84", textDecoration: "none" }}>
                  {e.display_name}
                </Link>
                {e.verification_status === "verified" && (
                  <BadgeCheck size={14} strokeWidth={2.4} color="#15803d" />
                )}
              </div>
              {e.bio && (
                <div style={{ color: "#666", fontSize: "0.84rem", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e.bio}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Вакансия
  return (
    <div style={{
      marginBottom: 18, padding: "14px 16px",
      background: "#fff1f6", border: "1px solid #ffc2d9", borderRadius: 10,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <UserPlus size={18} strokeWidth={2.2} color="#c4004f" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "#9c0040", fontSize: "0.95rem" }}>
            Раздел «{specialtyLabel || specialty}» формируется
          </div>
          <p style={{ color: "#9c0040", fontSize: "0.88rem", margin: "4px 0 0", lineHeight: 1.5 }}>
            Мы ищем ответственного редактора — практикующего врача, который возьмёт раздел под своё имя:
            отбор материалов, разбор случаев, ответы коллегам. Ваше имя и регалии будут указаны
            на каждой странице раздела.
          </p>

          {sent ? (
            <div style={{ marginTop: 10, color: "#15803d", fontWeight: 700, fontSize: "0.88rem" }}>
              Отклик отправлен — мы свяжемся с вами по email.
            </div>
          ) : showForm ? (
            <form onSubmit={apply} style={{ marginTop: 10 }}>
              <textarea
                value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={2000}
                placeholder="Коротко о себе: специализация, стаж, чем занимаетесь — и как видите раздел"
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ff8fb8",
                  fontSize: "0.88rem", boxSizing: "border-box", background: "#fff",
                }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button type="submit" disabled={busy} style={{
                  background: "#c4004f", color: "#fff", border: "none", borderRadius: 8,
                  padding: "7px 16px", fontWeight: 700, fontSize: "0.86rem", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6, marginTop: 0,
                }}>
                  <Send size={14} strokeWidth={2.2} /> {busy ? "Отправляем…" : "Отправить отклик"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} style={{
                  background: "none", border: "none", color: "#9c0040", fontWeight: 600,
                  fontSize: "0.86rem", cursor: "pointer", marginTop: 0,
                }}>
                  Отмена
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => (isLoggedIn ? setShowForm(true) : onLoginRequest && onLoginRequest())}
              style={{
                marginTop: 10, background: "#c4004f", color: "#fff", border: "none", borderRadius: 8,
                padding: "7px 16px", fontWeight: 700, fontSize: "0.86rem", cursor: "pointer",
              }}>
              Стать ответственным редактором раздела
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
